/**
 * "Liquid window" morph between the paper-plane CTA and the SMQ v2 pop-in card
 * (DLM-1770), based on the reference demo attached to the ticket
 * (popin-morph-navbar.html).
 *
 * The demo eased each keyframe segment to a near stop before kicking off the
 * next one, which read as the card opening "in 3 steps". We therefore split
 * the morph into two synchronized Web Animations:
 * - motion (translate/scale/opacity/shadow): 2 keyframes on one continuous
 *   curve, so the velocity never breaks;
 * - liquid (border-radius): the demo's organic "blobby" intermediate values.
 *
 * The drop shadow is suspended while the card is in motion: border-radius
 * keyframes already force a main-thread repaint on every frame, and painting
 * the 40px shadow blur on top is what makes the morph stutter.
 *
 * Both elements live in the fixed navbar tree, so getBoundingClientRect()
 * deltas stay consistent whatever the page scroll position.
 */

type MorphDelta = { dx: number; dy: number; sx: number; sy: number }

/** Center offset and size ratios from the card to the CTA. */
function measureDelta(cta: Element, card: Element): MorphDelta {
  const c = cta.getBoundingClientRect()
  const p = card.getBoundingClientRect()
  return {
    dx: c.left + c.width / 2 - (p.left + p.width / 2),
    dy: c.top + c.height / 2 - (p.top + p.height / 2),
    sx: c.width / p.width,
    sy: c.height / p.height,
  }
}

const prefersReducedMotion = () =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false

const OPEN_MS = 680
const CLOSE_MS = 560
// Continuous ease-out/ease-in curves: the whole travel decelerates (open) or
// accelerates (close) in one sweep — no per-segment velocity resets.
const OPEN_EASING = "cubic-bezier(.22,1,.36,1)"
const CLOSE_EASING = "cubic-bezier(.5,0,.75,0)"

function playFade(card: HTMLElement, direction: "in" | "out"): Animation[] {
  return [
    card.animate(
      direction === "in" ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 1 }, { opacity: 0 }],
      { duration: 150, easing: "ease", fill: direction === "in" ? "backwards" : "forwards" }
    ),
  ]
}

/**
 * Expands the card out of the CTA. Returns null when the Web Animations API is
 * unavailable (jsdom, legacy browsers): the card then simply appears in place.
 * Cancel every returned animation when interrupting; the first one carries the
 * timing (attach onfinish there).
 */
export function playOpenMorph(card: HTMLElement, cta: HTMLElement | null): Animation[] | null {
  if (typeof card.animate !== "function") {
    return null
  }
  if (!cta || prefersReducedMotion()) {
    return playFade(card, "in")
  }
  const { dx, dy, sx, sy } = measureDelta(cta, card)
  const restingShadow = getComputedStyle(card).boxShadow
  const timing = { duration: OPEN_MS, easing: OPEN_EASING, fill: "backwards" as const }
  return [
    card.animate(
      [
        {
          transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`,
          opacity: 0.5,
          boxShadow: "none",
        },
        { boxShadow: "none", offset: 0.7 },
        { transform: "translate(0, 0) scale(1, 1)", opacity: 1, boxShadow: restingShadow },
      ],
      timing
    ),
    card.animate(
      [
        { borderRadius: "50%" },
        { borderRadius: "42% 58% 46% 54% / 56% 46% 54% 44%", offset: 0.4 },
        { borderRadius: "30% 70% 32% 68% / 62% 36% 64% 38%", offset: 0.7 },
        { borderRadius: "16px" },
      ],
      timing
    ),
  ]
}

/**
 * Collapses the card back into the CTA. Returns null when the Web Animations
 * API is unavailable: the caller must then finish the close immediately.
 */
export function playCloseMorph(card: HTMLElement, cta: HTMLElement | null): Animation[] | null {
  if (typeof card.animate !== "function") {
    return null
  }
  if (!cta || prefersReducedMotion()) {
    return playFade(card, "out")
  }
  const { dx, dy, sx, sy } = measureDelta(cta, card)
  const restingShadow = getComputedStyle(card).boxShadow
  const timing = { duration: CLOSE_MS, easing: CLOSE_EASING, fill: "forwards" as const }
  return [
    card.animate(
      [
        { transform: "translate(0, 0) scale(1, 1)", opacity: 1, boxShadow: restingShadow },
        { boxShadow: "none", offset: 0.35 },
        {
          transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`,
          opacity: 0,
          boxShadow: "none",
        },
      ],
      timing
    ),
    card.animate(
      [
        { borderRadius: "16px" },
        { borderRadius: "34% 66% 36% 64% / 60% 38% 62% 40%", offset: 0.4 },
        { borderRadius: "54% 46% 58% 42% / 42% 56% 44% 58%", offset: 0.75 },
        { borderRadius: "50%" },
      ],
      timing
    ),
  ]
}
